import { useEffect, useState } from 'react';
import { PaymentType, usePrevious } from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useLazyGetCustomersTopUpInstructionPdfQuery,
  useLazyGetCustomersTopUpInstructionsQuery,
  useLazyGetCustomersTopUpQuery,
} from '../../../customers/store/services/customers';
import { TopUpInstructions, PspPartnerPaymentType } from '../../types';
import getPspPartnerPaymentType from '../helpers/get-psp-partner-type';
import { clearTopUpInstruction } from '../../store/slices/top-up-panel';

const useTopUpPanel = ({
  topUpOptionsValues,
  selectedTopUpId,
  userAccounts,
  setSelectedTopUpId,
  setTopUpPanelIsOpen,
  setSelectedAccount,
  topUpPanelIsOpen,
  selectedAccount,
  topUpInstrusctions,
}: {
  topUpOptionsValues?: {
    companyName: string;
    id: string;
    visibleToClients: boolean;
  }[];
  selectedTopUpId?: string;
  userAccounts?: {
    accountId: string;
    description: string;
    account: {
      fullNumber: string;
    };
  }[];
  setSelectedTopUpId: (data: string) => void;
  setTopUpPanelIsOpen: (data: boolean) => void;
  setSelectedAccount?: (data: string) => void;
  topUpPanelIsOpen?: boolean;
  selectedAccount?: string;
  topUpInstrusctions?: TopUpInstructions;
}) => {
  const [topUpCurrency, setTopUpCurrency] = useState('');
  const [topUpAccount, setTopUpAccount] = useState('');
  const [selectedInstructionType, setInstructionType] = useState('');
  const [topUpOptions, setTopUpOptions] = useState<
    {
      companyName: string;
      id: string;
      visibleToClients: boolean;
    }[]
  >([]);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const dispatch = useAppDispatch();

  const prevSearchText = usePrevious(searchText);

  const [getTopUp, { isLoading, isFetching }] = useLazyGetCustomersTopUpQuery();
  const [
    getCustomersTopUpInstructions,
    { isLoading: instructionsIsLoading, isFetching: instructionsIsFetching },
  ] = useLazyGetCustomersTopUpInstructionsQuery();
  const [
    getCustomersTopUpInstructionPdf,
    { isLoading: pdfDownloadInProgress },
  ] = useLazyGetCustomersTopUpInstructionPdfQuery();

  const onSearchTopUp = async ({
    text,
    cursor,
  }: {
    cursor?: string;
    text?: string;
  }) => {
    const body: { companyName?: string; cursor?: string } = {
      companyName: text || '',
      cursor: cursor || '',
    };

    if (cursor) {
      setNextPageIsLoading(true);
    }

    const response = await getTopUp(body);

    if (response && response.data && response.data.data) {
      if (text) {
        setTopUpOptions(response.data.data?.edges?.map((item) => item.node));
      } else if (!text && prevSearchText) {
        setTopUpOptions(
          topUpOptionsValues as {
            companyName: string;
            id: string;
            visibleToClients: boolean;
          }[],
        );
      }

      setNextPageIsLoading(false);
    }
  };

  const getCustomersTopUpInstructionPdfAction = async (
    index: number,
    instructionType: PspPartnerPaymentType,
  ) => {
    setInstructionType(instructionType);

    const response = await getCustomersTopUpInstructionPdf({
      id: topUpAccount,
      pspPartnerId: selectedTopUpId as string,
      currencyId: topUpCurrency,
      index,
    });

    let currentInstruction;

    if (topUpInstrusctions) {
      currentInstruction = topUpInstrusctions?.items[index];
    }

    if (response.data) {
      const currentAccount = userAccounts?.find(
        (account) => account.accountId === topUpAccount,
      );

      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${
        currentInstruction &&
        currentInstruction.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT
          ? `${currentInstruction?.casp?.name} Payment`
          : getPspPartnerPaymentType(instructionType)
      }(${currentAccount?.account.fullNumber}).pdf`;
      hiddenElement.click();

      setInstructionType('');
    }
  };

  const handleCloseTopUpSidepanel = () => {
    setTopUpAccount('');
    setSelectedTopUpId('');
    setTopUpCurrency('');
    setTopUpPanelIsOpen(false);
    setTopUpOptions([]);
    setSearchText('');
    setNextPageIsLoading(false);

    dispatch(clearTopUpInstruction());

    if (setSelectedAccount) {
      setSelectedAccount('');
    }
  };

  useEffect(() => {
    if (topUpPanelIsOpen && topUpOptionsValues) {
      setTopUpOptions(topUpOptionsValues);
    }
  }, [topUpOptionsValues, topUpPanelIsOpen]);

  useEffect(() => {
    if (topUpAccount && selectedTopUpId && topUpCurrency) {
      getCustomersTopUpInstructions({
        id: topUpAccount,
        pspPartnerId: selectedTopUpId,
        currencyId: topUpCurrency,
      });
    }
  }, [topUpAccount, selectedTopUpId, topUpCurrency]);

  useEffect(() => {
    if (selectedAccount) {
      setTopUpAccount(selectedAccount);
    }
  }, [selectedAccount]);

  return {
    handleCloseTopUpSidepanel,
    topUpOptions,
    topUpCurrency,
    setTopUpCurrency,
    topUpAccount,
    setTopUpAccount,
    instructionsIsLoading,
    instructionsIsFetching,
    getCustomersTopUpInstructionPdfAction,
    pdfDownloadInProgress,
    selectedInstructionType,
    onSearchTopUp,
    isLoading,
    isFetching,
    nextPageIsLoading,
    searchText,
    setSearchText,
  };
};

export default useTopUpPanel;
